import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUSES = ["new", "processing", "shipped", "delivered", "cancelled"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/orders/${id}`, { status });
    toast.success("Updated");
    load();
    if (selected && selected.id === id) setSelected({ ...selected, status });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Orders</h1>
      <Card className="border-zinc-200">
        <CardContent className="p-0">
          <Table data-testid="orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} onClick={() => setSelected(o)} className="cursor-pointer" data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                  <TableCell>{o.customer_name}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{o.created_at.slice(0, 10)}</TableCell>
                  <TableCell>{o.items.length}</TableCell>
                  <TableCell className="font-mono">{formatIDR(o.total)}</TableCell>
                  <TableCell>
                    <Badge variant={o.payment_status === "paid" ? "default" : "secondary"}>{o.payment_status}</Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {!orders.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-zinc-500">No orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.order_number}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs uppercase text-zinc-500">Customer</p>
                  <p>{selected.customer_name} · {selected.customer_email} · {selected.customer_phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-zinc-500">Shipping</p>
                  <p>{selected.shipping_address}, {selected.city} {selected.postal_code}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-zinc-500 mb-2">Items</p>
                  <ul className="space-y-1">
                    {selected.items.map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{it.name} × {it.quantity}</span>
                        <span className="font-mono">{formatIDR(it.price * it.quantity)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t pt-2 flex justify-between font-medium">
                  <span>Total</span><span className="font-mono">{formatIDR(selected.total)}</span>
                </div>
                {selected.notes && <p className="text-xs text-zinc-500">Note: {selected.notes}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
